import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { editLoan, deleteLoan } from '../../features/loans/loanAPI';
import useEthers from '../../hooks/useEthers';

export default function LoanCard(loan) {
    const pastLoans = 10;
    const activeLoans = 10;
    const creditUnlocked = 20;
    const totalLoans = pastLoans + activeLoans;
    const months = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const time = new Date(loan.createdAt).toISOString();
    const date = new Date(loan.createdAt);
    const today = new Date();
    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const timeDifference = today - date;
    const differenceInDays = Math.round(loan.days - (timeDifference / (1000 * 60 * 60 * 24)));
    const splt = time.split("T")[0].split("-");
    const pastLoansPercentage = (pastLoans / totalLoans) * 100;
    const activeLoansPercentage = (activeLoans / totalLoans) * 100;
    const [repaymentAmount, setRepaymentAmount] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [help, setHelp] = useState('');
    const [user, setUser] = useState('');
    const username = useSelector((state) => state.auth.username);
    const { switchNetwork, Transfer } = useEthers();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (loan.lenderUser === username && loan.borrowerUser) {
            setUser(loan.borrowerUser);
        } else if (loan.borrowerUser === username && loan.lenderUser) {
            setUser(loan.lenderUser);
        } else {
            setUser('Anonymous');
        }
        if (loan.lenderUser === username && loan.loanStatus === 'Requested' && loan.borrowerUser) {
            setHelp("Send Your Help");
        } else if (loan.borrowerUser === username && loan.loanStatus === 'Lent' && loan.lenderUser) {
            setHelp("Repay Their Help");
        } else if ((!loan.lenderUser && loan.borrowerUser === username) || (loan.borrowerUser === username && loan.loanStatus === 'Requested' && loan.lenderUser)) {
            setHelp("Wait For Lenders");
        } else if (!loan.borrowerUser && loan.lenderUser === username) {
            setHelp("Wait For Borrowers");
        } else {
            setHelp(`${loan.loanStatus}, ${loan.repaymentStatus}`);
        }
    }, [loan, username]);

    const handleClick = (e) => {
        e.preventDefault();
        if (loan.lenderUser === username && loan.loanStatus === 'Requested' && loan.borrowerUser) {
            handleLend(e);
        } else if (loan.borrowerUser === username && loan.loanStatus === 'Lent' && loan.repaymentStatus !== 'Paid' && loan.lenderUser) {
            handleBorrow(e);
        }
    };

    const handleBorrow = async (e) => {
        e.preventDefault();
        const loanData = {
            _id: loan._id,
            repaymentAmount: parseInt(loan.repaymentAmount) + parseInt(repaymentAmount),
            repaymentStatus: parseInt(loan.repaymentAmount) + parseInt(repaymentAmount) < parseInt(loan.repayedAmount) ? 'Partial' : 'Paid',
            loanStatus: loan.loanStatus
        };
        if (parseInt(loan.repaymentAmount) + parseInt(repaymentAmount) <= parseInt(loan.repayedAmount) && loan.loanStatus === 'Lent' && loan.repaymentStatus !== 'Paid' && repaymentAmount > 0) {
            await switchNetwork(loan.block);
            const cdt = await Transfer(e, loan.lenderWallet, repaymentAmount.toString(), loan._id, loan.block, loan.coin);
            try {
                cdt && await dispatch(editLoan(loanData))
                    .unwrap()
                    .then(() => {
                        alert('Loan edited successfully');
                        setShowModal(false);
                    })
                    .catch((error) => {
                        alert('Error editing loan:', error.message || error);
                    });
            }
            catch (error) {
                cdt && await dispatch(editLoan(loanData))
                    .unwrap()
                    .then(() => {
                        alert('Loan edited successfully');
                    })
                    .catch((error) => {
                        alert('Error editing loan:', error.message || error);
                    });
            }
        }
        setRepaymentAmount('');
    };

    const handleLend = async (e) => {
        e.preventDefault();
        const loanData = {
            _id: loan._id,
            repaymentAmount: loan.repaymentAmount,
            repaymentStatus: loan.repaymentStatus,
            loanStatus: 'Lent'
        };
        if (loan.loanStatus === 'Requested') {
            const cdt = await Transfer(e, loan.borrowerWallet, loan.loanAmount.toString(), loan._id);
            try {
                cdt && await dispatch(editLoan(loanData))
                    .unwrap()
                    .then(() => {
                        console.log('Loan edited successfully');
                    })
                    .catch((error) => {
                        console.error('Error editing loan:', error.message || error);
                    });
            }
            catch (error) {
                cdt && await dispatch(editLoan(loanData))
                    .unwrap()
                    .then(() => {
                        console.log('Loan edited successfully');
                    })
                    .catch((error) => {
                        console.error('Error editing loan:', error.message || error);
                    });
            }
        }
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        const _id = loan._id;
        await dispatch(deleteLoan({ _id }))
            .unwrap()
            .then(() => {
                alert('Loan deleted successfully');
                navigate('/profile');
            })
            .catch((error) => {
                alert('Error deleting loan:', error.message || error);
            });
    };

    return (
        <article className="flex flex-col py-7 w-full bg-white rounded-3xl border border-solid border-neutral-200 shadow-[0px_2px_20px_rgba(0,0,0,0.25)]">
            <header className="flex gap-5 justify-between mx-8 max-md:mx-2.5">
                <div className="flex flex-col">
                    <div className="flex gap-1.5 items-center text-xl font-medium whitespace-nowrap text-slate-900">
                        <span>
                            {/* {loan.borrowerUser !== username && <svg className="float-left" width="25px" height="25px" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="#000000" stroke-width="2.56"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><polyline points="8 36 16 36 16 52 8 52"></polyline><path d="M16 38h8a4 4 0 0 1 4 4h9a3 3 0 0 1 3 3 3 3 0 0 1-3 3H26"></path><path d="m40 44.8 6.63-4a5.82 5.82 0 0 1 3-.82 5.78 5.78 0 0 1 4.09 1.69L56 44 38.07 54.76A8.75 8.75 0 0 1 33.58 56a8.68 8.68 0 0 1-3.06-.56L16 50"></path><circle cx="38" cy="22" r="14"></circle><path d="M36.33 22a3.34 3.34 0 0 1 0-6.67"></path><path d="M39.67 22a3.34 3.34 0 0 1 0 6.67"></path><line x1="38" y1="12" x2="38" y2="15.33"></line><line x1="38" y1="28.67" x2="38" y2="32"></line><line x1="36.33" y1="22" x2="39.67" y2="22"></line><path d="M43 17s-1.67-1.67-3.33-1.67h-3.34"></path><path d="M33 27s1.67 1.67 3.33 1.67h3.34"></path></g></svg>}
                            {loan.lenderUser !== username && <svg className="float-left" width="25px" height="25px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M12.37 2.15009L21.37 5.75006C21.72 5.89006 22 6.31006 22 6.68006V10.0001C22 10.5501 21.55 11.0001 21 11.0001H3C2.45 11.0001 2 10.5501 2 10.0001V6.68006C2 6.31006 2.28 5.89006 2.63 5.75006L11.63 2.15009C11.83 2.07009 12.17 2.07009 12.37 2.15009Z" stroke="#000000" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M22 22H2V19C2 18.45 2.45 18 3 18H21C21.55 18 22 18.45 22 19V22Z" stroke="#000000" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M4 18V11" stroke="#000000" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M8 18V11" stroke="#000000" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M12 18V11" stroke="#000000" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M16 18V11" stroke="#000000" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M20 18V11" stroke="#000000" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M1 22H23" stroke="#000000" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M12 8.5C12.8284 8.5 13.5 7.82843 13.5 7C13.5 6.17157 12.8284 5.5 12 5.5C11.1716 5.5 10.5 6.17157 10.5 7C10.5 7.82843 11.1716 8.5 12 8.5Z" stroke="#000000" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>} */}
                            {user}
                        </span>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.5105 0.489502L12.9909 4.18512L17.2665 2.94849L16.7911 7.37388L20.8613 9.17487L17.6526 12.2593L19.6129 16.2552L15.1722 16.5555L14.1053 20.8766L10.5105 18.2522L6.9157 20.8766L5.84885 16.5555L1.40814 16.2552L3.36843 12.2593L0.159678 9.17487L4.22987 7.37388L3.75448 2.94849L8.03008 4.18512L10.5105 0.489502Z" fill="#2154E8" />
                            <path d="M7.19336 10.3882L9.95928 13.2997L13.8316 9.22363" stroke="white" stroke-width="1.10637" stroke-linecap="round" />
                        </svg>
                        {/* <svg
                            aria-label="Verified User Badge"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="w-5 h-5 text-blue-500"
                            fill="currentColor"
                        >
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                        </svg> */}
                    </div>
                    <time
                        dateTime={time}
                        className="text-base leading-loose text-neutral-600 text-opacity-80"
                    >
                        {months[parseInt(splt[1])] + " " + splt[2] + ", " + splt[0]}
                    </time>
                </div>
                {loan.lenderUser === username && (
                    <span
                        className="px-5 py-2 my-auto text-base font-medium text-white whitespace-nowrap bg-red-600 rounded-xl"
                        role="status"
                    >
                        Urgent
                    </span>
                )}
                {loan.loanStatus === 'Requested' && (loan.lenderUser === username || loan.borrowerUser === username) && (<>
                    <svg onClick={() => setShowModal(true)} width="25px" height="25px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#ff0000">
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                            <path d="M10 12V17" stroke="#ff0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M14 12V17" stroke="#ff0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M4 7H20" stroke="#ff0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M6 10V18C6 19.6569 7.34315 21 9 21H15C16.6569 21 18 19.6569 18 18V10" stroke="#ff0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z" stroke="#ff0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                        </g>
                    </svg>
                    {showModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <button onClick={() => setShowModal(false)} className="text-gray-600 hover:text-gray-800 fixed top-4 right-4 z-50">
                            ✖
                        </button>
                        <main className="flex flex-col py-7 w-full bg-white rounded-3xl border border-solid border-neutral-200 shadow-[0px_2px_8px_rgba(0,0,0,0.25)] flex overflow-hidden flex-col py-5 bg-white rounded-2xl max-w-[473px]">
                            <section className="flex flex-col px-5 w-full">
                                <h1 className="self-start text-2xl font-medium leading-none text-black">
                                    Delete Loan Request?
                                </h1>
                                <p className="mt-5 text-sm leading-6 text-gray-500 text-opacity-60">
                                    Are you sure you want to delete this loan request? This action cannot be undone.
                                </p>
                                <div className="self-end">
                                    <button onClick={() => setShowModal(false)} className="mt-5 mr-2 overflow-hidden flex-auto gap-5 self-stretch px-5 py-3.5 rounded-lg min-h-[46px] bg-white text-black border border-solid">
                                        Cancel
                                    </button>
                                    <button onClick={(e) => handleDelete(e)} className="overflow-hidden flex-auto gap-5 self-stretch px-5 py-3.5 rounded-lg min-h-[46px] bg-red-500 text-white border border-solid">
                                        Delete Request
                                    </button>
                                </div>
                            </section>
                        </main>
                    </div>}
                </>)}
            </header>

            <hr className="shrink-0 mt-5 h-px border-solid border-neutral-400" />

            <div className="flex flex-col px-8 mt-4 w-full max-md:px-5 max-md:max-w-full">
                <h2 className="self-start text-xl font-medium leading-8 text-slate-900">
                    {loan.reason}
                </h2>

                <div className="flex gap-2 items-center mt-3" role="progressbar" aria-label="Loan History">
                    <div
                        className="flex h-2.5 bg-green-500 rounded-[100px]"
                        style={{ width: `${pastLoansPercentage}%` }}
                        aria-label={`Past Loans: ${pastLoans}`}
                    />
                    <div
                        className="flex h-2.5 bg-orange-500 rounded-[100px]"
                        style={{ width: `${activeLoansPercentage}%` }}
                        aria-label={`Active Loans: ${activeLoans}`}
                    />
                </div>
                <div className="flex gap-10 mt-6 w-full text-base text-slate-900">
                    <div className="flex flex-1 gap-2.5 items-center">
                        <div className="flex w-3 h-3 bg-green-500 rounded-full" aria-hidden="true" />
                        <span>Past loans : {pastLoans}</span>
                    </div>
                    <div className="flex flex-1 gap-2.5 items-center">
                        <div className="flex w-3 h-3 bg-orange-500 rounded-full" aria-hidden="true" />
                        <span>Active loans : {activeLoans}</span>
                    </div>
                </div>

                {loan.repaymentAmount ? (
                    <div
                        className="flex overflow-hidden flex-col justify-center px-4 py-3.5 mt-5 w-full text-base text-red-600 bg-red-100 rounded-lg"
                        role="alert"
                    >
                        <div className="flex gap-1.5 justify-center items-center">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-6 h-6"
                                aria-hidden="true"
                            >
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                            </svg>
                            <span className="font-medium">
                                ${loan.repaymentAmount} defaulted on Previous Repayment
                            </span>
                        </div>
                    </div>
                ) : (
                    <div
                        className="flex overflow-hidden flex-col justify-center px-4 py-3.5 mt-5 w-full text-base text-green-600 bg-green-100 rounded-lg"
                        role="alert"
                    >
                        <div className="flex gap-1.5 justify-center items-center">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-6 h-6"
                                aria-hidden="true"
                            >
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                            </svg>
                            <span className="font-medium">
                                Not defaulted on Previous Repayment
                            </span>
                        </div>
                    </div>
                )}

                <div className="flex gap-2.5 items-center self-start mt-5 text-base text-green-600">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-6 h-6"
                        aria-hidden="true"
                    >
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
                    </svg>
                    <span>${creditUnlocked.toFixed(2)} credit unlocked</span>
                </div>
                <div className="flex gap-5 justify-between mt-11">
                    <div className="flex flex-col whitespace-nowrap">
                        <span className="text-base text-neutral-700 text-opacity-60">Amount</span>
                        <span className="text-xl text-slate-900">${loan.repayedAmount}</span>
                    </div>
                    <div className="shrink-0 self-end mt-6 w-px border border-solid border-neutral-400 h-[34px]" aria-hidden="true" />
                    <div className="flex flex-col whitespace-nowrap">
                        <span className="text-base text-neutral-700 text-opacity-60">Payback</span>
                        <span className="text-xl text-slate-900">${loan.repaymentAmount}</span>
                    </div>
                    <div className="shrink-0 self-end mt-6 w-px border border-solid border-neutral-400 h-[34px]" aria-hidden="true" />
                    <div className="flex flex-col">
                        <span className="text-base text-neutral-700 text-opacity-60">Days</span>
                        <div className="text-xl text-slate-900">
                            <span>{loan.days}</span>
                            <span className="text-base text-red-600"> ({differenceInDays > 0 ? differenceInDays : "0"} {differenceInDays > 1 ? "days" : "day"} left)</span>
                        </div>
                    </div>
                </div>
                {help.includes("Repay") && <input
                    type="number"
                    min="0"
                    className="gap-5 self-stretch p-5 mt-4 text-lg leading-none bg-white rounded-lg border border-solid border-zinc-300 text-black text-opacity-60 max-md:px-5"
                    placeholder="Enter your payback amount"
                    value={repaymentAmount}
                    onChange={(e) => setRepaymentAmount(e.target.value)}
                />}
                <div className="flex gap-4 mt-11 w-full text-base text-center">
                    <img
                        onClick={() => navigate('/user/' + loan.borrowerUser)}
                        src="https://cdn.builder.io/api/v1/image/assets/e485b3dc4b924975b4554885e21242bb/2cdc152d20e346dc036028caab4febceaa703fc59283d1cc6ddab0883511f093?apiKey=e485b3dc4b924975b4554885e21242bb&"
                        alt="User profile"
                        className="object-contain shrink-0 rounded-md aspect-square w-[46px]"
                    />
                    <button
                        className={`overflow-hidden flex-auto gap-5 self-stretch px-5 py-3.5 rounded-lg min-h-[46px] ${help.includes("Help") ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border border-blue-600 border-solid'}`}
                        aria-label="Send help to this user"
                        onClick={handleClick}
                    >
                        {help}
                    </button>
                </div>
            </div>
        </article>
    );
}